// Shared, pure radius-geometry helpers.
//
// Intentionally free of any Vue/component dependency (and any third-party
// library) so that fast-check property tests can import and exercise them
// directly, and so multiple features can share one radius-to-polygon
// implementation.
//
// Used by:
//   - placeSelectionCsv.js (CSV Radius upload preview)
//   - components/places/utils/pointRadiusConversion.js (Places Action Modal
//     point-to-area conversion)

/**
 * The radius units supported by {@link radiusToKm} / {@link squarePolygonFor}.
 * These are the exact string values UI unit selectors must emit.
 * @type {['miles', 'kilometers']}
 */
export const RADIUS_UNITS = ["miles", "kilometers"];

/**
 * The default radius unit shared across radius UIs.
 * @type {'miles'}
 */
export const DEFAULT_RADIUS_UNIT = "miles";

/**
 * Convert a radius expressed in a supported unit to kilometers.
 *
 * Pure and turf-free: `miles` are scaled by the exact factor `1.609344`, while
 * `kilometers` pass through unchanged. Any other unit is unsupported and yields
 * `null` so callers can treat it as a rejection.
 *
 * @param {number} radiusValue - A positive finite radius in the given unit.
 * @param {'miles'|'kilometers'} unit - The distance unit for `radiusValue`.
 * @returns {number|null} The radius in kilometers, or null when `unit` is unsupported.
 */
export function radiusToKm(radiusValue, unit) {
  if (unit === "miles") {
    return radiusValue * 1.609344;
  }

  if (unit === "kilometers") {
    return radiusValue;
  }

  return null;
}

/**
 * Number of kilometers per degree of latitude (and of longitude at the
 * equator) used by the flat-earth offset below.
 * @type {number}
 */
const KM_PER_DEGREE = 111.32;

/**
 * Build an axis-aligned square (bounding-box style) GeoJSON `Polygon` geometry
 * centered on `[lon, lat]`, with each edge `radiusValue` (in `unit`) from the
 * center. The returned value is a bare geometry (no `Feature` wrapper) so it
 * composes cleanly; callers apply the feature wrapper with `properties`.
 *
 * The corners are computed with a local equirectangular ("flat-earth") offset
 * rather than a geodesic calculation, keeping this function pure and turf-free:
 *
 *   km   = radiusToKm(radiusValue, unit)
 *   dLat = km / 111.32                              // degrees latitude per km
 *   dLon = km / (111.32 * cos(lat * PI / 180))      // degrees longitude per km at this lat
 *
 * This approximation is accurate near the equator and degrades toward the poles
 * and for very large radii, which is acceptable for a client-side, preview-only
 * overlay. The ring is emitted closed (first === last) in SW, SE, NE, NW, SW
 * corner order.
 *
 * Returns `null` when the input is rejected: `radiusValue` is not a finite
 * number greater than 0, `lon` is outside `[-180, 180]`, `lat` is outside
 * `[-90, 90]`, or `unit` is unsupported (i.e. {@link radiusToKm} returns null).
 *
 * @param {number} lon - Longitude in [-180, 180].
 * @param {number} lat - Latitude in [-90, 90].
 * @param {number} radiusValue - A finite radius greater than 0.
 * @param {'miles'|'kilometers'} unit - The distance unit for `radiusValue`.
 * @returns {{type:'Polygon', coordinates:number[][][]}|null} The square Polygon geometry, or null when rejected.
 */
export function squarePolygonFor(lon, lat, radiusValue, unit) {
  // Reject a non-positive or non-finite radius.
  if (!Number.isFinite(radiusValue) || radiusValue <= 0) {
    return null;
  }

  // Reject out-of-range coordinates.
  if (!Number.isFinite(lon) || lon < -180 || lon > 180) {
    return null;
  }
  if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
    return null;
  }

  // Reject unsupported units (radiusToKm returns null for those).
  const km = radiusToKm(radiusValue, unit);
  if (km === null) {
    return null;
  }

  const dLat = km / KM_PER_DEGREE;
  const dLon = km / (KM_PER_DEGREE * Math.cos((lat * Math.PI) / 180));

  return {
    type: "Polygon",
    coordinates: [
      [
        [lon - dLon, lat - dLat], // SW
        [lon + dLon, lat - dLat], // SE
        [lon + dLon, lat + dLat], // NE
        [lon - dLon, lat + dLat], // NW
        [lon - dLon, lat - dLat], // closure (identical to SW)
      ],
    ],
  };
}
